/* eslint-disable no-nested-ternary */
import { ActionIcon, Anchor, Breadcrumbs, Divider, Group, SimpleGrid, Stack, Tabs, Text } from '@mantine/core';
import { IconAt, IconEdit, IconMapPin, IconPhone, IconWorldWww } from '@tabler/icons';
import { FC, useContext } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { GrayBoxV2 } from '../../../Atoms/GrayBox';
import { ThemeText } from '../../../Atoms/ThemeText';
import { printify } from '../../../Utils/utils';
import { WindowContext } from '../../../Service/Context/WindowContext';
import { useContactDetails } from '../../../Service/Context/ContactDetailsContext';
import BookitupAddIcon from '../../../Atoms/BookitupAddIcon';
import PanePaper from '../../../Molecules/PanePaper';
import { openCreateEventModal } from '../../Event/CreateEventModal';
import BookitupBadge from '../../../Atoms/BookitupBadge';
import { LocationS } from '../../../Service/LocationS';
import { LocationDto } from '../../../Types/LocationT';
import { Contact } from '../../../Service/ContactS';
import ContactEvents, { ContactDatelessEvents, ContactRecommendationEvents } from './ContactEvents';
import ContactNoteList from './ContactNoteList';
import ContactTasks from './ContactTasks';

const ContactDetailsSection = () => {
  const { sm } = useContext(WindowContext);
  const navigate = useNavigate();
  const { loading, contact } = useContactDetails();
  const { id: contactId, personDescription } = contact ?? {};

  if (!contact) return null;

  return (
    <>
      {!sm && (
        <>
          <Breadcrumbs>
            <Anchor onClick={() => navigate('/contacts')}> Kontakte</Anchor>
            <Anchor sx={{ pointerEvents: 'none' }}>
              <b>{loading ? 'Laden ...' : personDescription ?? 'Unknown'}</b>
            </Anchor>
          </Breadcrumbs>
          <Divider my="sm" />
        </>
      )}
      <Stack spacing="xs">
        <PersonalInfoPane />
        {contactId && <ContactNoteList contactId={contactId.toString()} />}
        {contactId && <ContactTasks contactId={contactId} />}
        <PanePaper title="Events" rightSection={<BookitupAddIcon onClick={() => openCreateEventModal(contact)} />}>
          <Tabs defaultValue="events">
            <Tabs.List>
              <Tabs.Tab value="events">Chronologisch</Tabs.Tab>
              <Tabs.Tab value="dateless">Ohne Datum</Tabs.Tab>
              <Tabs.Tab value="recommendations">Vermittlungen</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="events" mt="sm">
              <ContactEvents />
            </Tabs.Panel>
            <Tabs.Panel value="dateless" mt="sm">
              <ContactDatelessEvents />
            </Tabs.Panel>
            <Tabs.Panel value="recommendations" mt="sm">
              <ContactRecommendationEvents />
            </Tabs.Panel>
          </Tabs>
        </PanePaper>
      </Stack>
    </>
  );
};

const PersonalInfoPane = () => {
  const { contact, openContactModal } = useContactDetails();
  const { contactType, companyName, partner } = contact ?? {};
  const isCompany = contactType === 'COMPANY';

  return (
    <GrayBoxV2
      rightSection={
        <ActionIcon onClick={openContactModal}>
          <IconEdit size={20} />
        </ActionIcon>
      }
    >
      <SimpleGrid cols={2} breakpoints={[{ maxWidth: 600, cols: 1, spacing: 'sm' }]}>
        <div>
          {isCompany && (
            <ThemeText size="xl" mb="md">
              {printify(companyName, '-')}
            </ThemeText>
          )}
          {contact && <PersonDescription contact={contact} />}
        </div>
        {partner && <PersonDescription contact={partner} />}
      </SimpleGrid>
    </GrayBoxV2>
  );
};

const PersonDescription: FC<{ contact: Contact }> = ({ contact }) => {
  const { salutation, firstName, lastName, birthday, email, phoneNumber, groups, website, contactType } = contact ?? {};
  const isPersonal = contactType === 'PRIVATE';

  return (
    <Stack spacing="xs">
      <ThemeText size="lg">{`${printify(salutation)} ${printify(firstName)} ${printify(lastName)}`}</ThemeText>

      {birthday && <ThemeText>🎂 {moment(birthday).format('DD.MM.YYYY')}</ThemeText>}

      {email && (
        <ThemeText>
          <IconAt size={16} style={{ marginRight: 5, marginBottom: 3 }} />
          {email}
        </ThemeText>
      )}
      {phoneNumber && (
        <ThemeText>
          <IconPhone size={16} style={{ marginRight: 5, marginBottom: 3 }} />
          {phoneNumber}
        </ThemeText>
      )}
      {website && isPersonal && (
        <div>
          <Text color="blue" component="a" href={website}>
            <IconWorldWww size={16} style={{ marginRight: 5, marginBottom: 3 }} />
            {website}
          </Text>
        </div>
      )}
      {isPersonal && <AddressDescription contact={contact} />}
      {groups && isPersonal && (
        <Group my="xs">
          {groups?.map((g) => (
            <BookitupBadge key={g} title={g} />
          ))}
        </Group>
      )}
    </Stack>
  );
};

const AddressDescription: FC<{ contact: Contact }> = ({ contact }) => {
  const { addressStreetAndNo, zipCode, city } = contact ?? {};
  const description = LocationS.getAddressDescription({
    streetAndNo: addressStreetAndNo,
    zipCode,
    city,
  } as LocationDto);

  if (!description) return null;

  return (
    <ThemeText>
      <IconMapPin size={16} style={{ marginRight: 5, marginBottom: 3 }} />
      {description}
    </ThemeText>
  );
};

export default ContactDetailsSection;
